import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { AuthService } from '../../core/auth/auth.service';
import { version } from '../../../../package.json';
import {
  type AccessRule,
  canAccess,
  PAGE_ACCESS,
} from '../../core/auth/access-control';
import { GlobalOverlayService } from '../../core/ui/global-overlay.service';

interface MenuItem {
  path: string;
  icon: string;
  label: string;
  access: AccessRule;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, LucideAngularModule],
  templateUrl: './main-layout.component.html',
})
export class MainLayout {
  auth = inject(AuthService);
  private overlay = inject(GlobalOverlayService);
  menu = signal(false);
  sidebarCollapsed = signal(false);
  
  readonly appVersion = version;

  private readonly menuSections: MenuSection[] = [
    {
      title: 'ภาพรวม',
      items: [
        {
          path: '/dashboard/sales',
          icon: 'House',
          label: 'แดชบอร์ดฝ่ายขาย',
          access: PAGE_ACCESS.salesDashboard,
        },
        {
          path: '/dashboard/accounting',
          icon: 'CircleDollarSign',
          label: 'แดชบอร์ดบัญชี',
          access: PAGE_ACCESS.accountingDashboard,
        },
      ],
    },
    {
      title: 'ข้อมูลการจอง',
      items: [
        {
          path: '/customers',
          icon: 'Users',
          label: 'ลูกค้า',
          access: PAGE_ACCESS.customers,
        },
        {
          path: '/bookings',
          icon: 'BookOpenCheck',
          label: 'รายการจองจอยทัวร์',
          access: PAGE_ACCESS.bookings,
        },
        {
          path: '/bookings/group',
          icon: 'UsersRound',
          label: 'รายการจองเหมากรุ๊ป',
          access: PAGE_ACCESS.groupBookings,
        },
        {
          path: '/tours',
          icon: 'Map',
          label: 'รายการทัวร์บริษัท',
          access: PAGE_ACCESS.tours,
        },
        {
          path: '/expenses/general',
          icon: 'ReceiptText',
          label: 'รายการเบิกค่าใช้จ่ายทั่วไป',
          access: PAGE_ACCESS.generalExpenses,
        },
        {
          path: '/expenses/requests',
          icon: 'FileText',
          label: 'รายการเบิกทั้งหมด',
          access: PAGE_ACCESS.expenseRequests,
        },

       /*   {
          path: '/expenses',
          icon: 'FileText',
          label: 'ขอเบิกรายการจอง',
          access: PAGE_ACCESS.expenses,
        },
        {
          path: '/commissions',
          icon: 'BadgePercent',
          label: 'ขอเบิกคอมมิชชั่น',
          access: PAGE_ACCESS.commissions,
        }, */
      ],
    },
    {
      title: 'การเงิน',
      items: [
        {
          path: '/accounting/cash-flow',
          icon: 'WalletCards',
          label: 'รายการรับ–จ่าย',
          access: PAGE_ACCESS.cashFlow,
        },
        {
          path: '/accounting/income',
          icon: 'ArrowDownLeft',
          label: 'รายการรายรับ',
          access: PAGE_ACCESS.income,
        },
        {
          path: '/accounting/expense',
          icon: 'ArrowUpRight',
          label: 'รายการรายจ่าย',
          access: PAGE_ACCESS.accountingExpense,
        },
        {
          path: '/commissions/recoveries',
          icon: 'History',
          label: 'ยอดหักคืนค่าคอม',
          access: PAGE_ACCESS.commissionRecoveries,
        },
        /* {
          path: '/payments',
          icon: 'Banknote',
          label: 'การชำระเงิน',
          access: PAGE_ACCESS.payments,
        }, */
        {
          path: '/payments/review',
          icon: 'CircleDollarSign',
          label: 'ตรวจสอบการชำระเงิน',
          access: PAGE_ACCESS.paymentReview,
        },

        {
          path: '/expenses/review',
          icon: 'ClipboardCheck',
          label: 'ตรวจสอบใบขอเบิก',
          access: PAGE_ACCESS.expenseReview,
        },

        {
          path: '/commissions/review',
          icon: 'ClipboardCheck',
          label: 'ตรวจสอบคอมมิชชั่น',
          access: PAGE_ACCESS.commissionReview,
        },
        {
          path: '/refunds/review',
          icon: 'Undo2',
          label: 'ตรวจสอบการคืนเงิน',
          access: PAGE_ACCESS.refundReview,
        },
      ],
    },
    {
      title: 'งานอนุมัติ',
      items: [
        {
          path: '/approvals/expenses',
          icon: 'ClipboardCheck',
          label: 'อนุมัติใบขอเบิก',
          access: PAGE_ACCESS.expenseApproval,
        },
        {
          path: '/approvals/commissions',
          icon: 'BadgePercent',
          label: 'อนุมัติคอมมิชชั่น',
          access: PAGE_ACCESS.commissionApproval,
        },
        {
          path: '/approvals/refunds',
          icon: 'Undo2',
          label: 'อนุมัติการคืนเงิน',
          access: PAGE_ACCESS.refundApproval,
        },
        {
          path: '/approvals/financial-adjustments',
          icon: 'WalletCards',
          label: 'อนุมัติปรับวงเงิน',
          access: PAGE_ACCESS.financialAdjustmentApproval,
        },
      ],
    },
  ];

  readonly visibleMenuSections = computed(() =>
    this.menuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => canAccess(this.auth.user(), item.access)),
      }))
      .filter((section) => section.items.length > 0),
  );

  async confirmLogout(): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ออกจากระบบ',
      message: 'คุณต้องการออกจากระบบหรือไม่',
      confirmText: 'ออกจากระบบ',
      cancelText: 'ยกเลิก',
    });

    if (!confirmed) {
      return;
    }

    await this.auth.logout();
  }


  initials() {
    const u = this.auth.user();
    return u ? `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}` : 'U';
  }


}
